import React from 'react';
import {Box, Stack, Tooltip, Typography} from '@mui/material';
import {
    COLLECTION_EEI_STATUS,
    COLLECTION_EEI_STATUS_COLORS,
} from '../buildCollectionCoverageView';

function segmentFillColor(status) {
    return COLLECTION_EEI_STATUS_COLORS[status] || COLLECTION_EEI_STATUS_COLORS[COLLECTION_EEI_STATUS.UNCOVERED];
}

function summaryTooltip(segments) {
    if (!segments?.length) {
        return 'No EEI collection coverage';
    }
    const counts = segments.reduce((acc, seg) => {
        acc[seg.status] = (acc[seg.status] || 0) + 1;
        return acc;
    }, {});
    const parts = [];
    if (counts[COLLECTION_EEI_STATUS.UNCOVERED]) {
        parts.push(`${counts[COLLECTION_EEI_STATUS.UNCOVERED]} not covered`);
    }
    if (counts[COLLECTION_EEI_STATUS.PARTIAL]) {
        parts.push(`${counts[COLLECTION_EEI_STATUS.PARTIAL]} partially covered`);
    }
    if (counts[COLLECTION_EEI_STATUS.COVERED]) {
        parts.push(`${counts[COLLECTION_EEI_STATUS.COVERED]} covered`);
    }
    return parts.join(' • ');
}

const EEI_STATUS_SORT_ORDER = {
    [COLLECTION_EEI_STATUS.UNCOVERED]: 0,
    [COLLECTION_EEI_STATUS.PARTIAL]: 1,
    [COLLECTION_EEI_STATUS.COVERED]: 2,
};

function sortSegmentsByStatus(segments) {
    return [...segments].sort((a, b) => {
        const orderA = EEI_STATUS_SORT_ORDER[a.status] ?? 0;
        const orderB = EEI_STATUS_SORT_ORDER[b.status] ?? 0;
        if (orderA !== orderB) {
            return orderA - orderB;
        }
        return (a.eeiIndex ?? 0) - (b.eeiIndex ?? 0);
    });
}

/**
 * Segmented collection coverage bar: one equal segment per EEI.
 * Green = covered, yellow = partially covered, red = not covered.
 */
export default function CollectionCoverageEeiBar({
    segments = [],
    dense = false,
    fullWidth = false,
    showCaption = false,
    caption = 'Collection coverage by EEI',
}) {
    if (!segments.length) {
        return null;
    }

    const sortedSegments = sortSegmentsByStatus(segments);

    const barHeight = dense ? 4 : (fullWidth ? 8 : 6);
    const gap = dense ? 0.5 : 1;
    const widthSx = fullWidth
        ? {width: '100%'}
        : {minWidth: dense ? 72 : 88, maxWidth: dense ? 120 : 160};

    return (
        <Stack spacing={dense ? 0.35 : 0.5} sx={widthSx}>
            {showCaption && (
                <Typography
                    variant="caption"
                    fontSize={dense ? '0.55rem' : '0.58rem'}
                    letterSpacing={0.5}
                    textTransform="uppercase"
                    color="text.disabled"
                >
                    {caption}
                </Typography>
            )}
            <Tooltip title={summaryTooltip(sortedSegments)} placement="bottom" arrow>
                <Box
                    data-testid="collection-coverage-eei-bar"
                    sx={{
                        display: 'flex',
                        gap: `${gap}px`,
                        width: '100%',
                        height: barHeight,
                        bgcolor: '#eeeeee',
                        borderRadius: 0.5,
                        overflow: 'hidden',
                        cursor: 'default',
                    }}
                >
                    {sortedSegments.map((segment, index) => (
                        <Box
                            key={segment.eeiId || segment.eeiNumber || segment.eeiIndex || index}
                            sx={{
                                flex: 1,
                                minWidth: 3,
                                height: '100%',
                                bgcolor: segmentFillColor(segment.status),
                            }}
                        />
                    ))}
                </Box>
            </Tooltip>
        </Stack>
    );
}
